import { formatFloatAsPercent } from '@gonfalon/format/formatFloatAsPercent';
import { formatNumber } from '@gonfalon/format/formatNumber';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Focusable, ProgressBar, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { DEFAULT_USAGE_PRECISION } from '../../../../constants';
import type { AggregationType, UsageSeriesList, UsageTabKey } from '../../../../types';
import { Granularity } from '../../../../types';
import { useUsageData } from '../../../hooks/useUsageData';
import { getSeriesTotal } from '../../../utils/getSeriesTotal';

import styles from './UsageSummaryCard.module.css';

type UsageSummaryCardProps = {
  usageTab: UsageTabKey;
  title: string;
  titleTooltip?: string;
  total: number;
  previousFromTimestampUTC: number;
  previousToTimestampUTC: number;
  aggregationType: AggregationType;
  percentChangeTooltip?: string;
  isTotalLoading?: boolean;
  isTotalError?: boolean;
  precision?: number;
};

export function UsageSummaryCard({
  usageTab,
  title,
  titleTooltip,
  total,
  previousFromTimestampUTC,
  previousToTimestampUTC,
  aggregationType,
  isTotalLoading = false,
  isTotalError = false,
  percentChangeTooltip = msg('billing.usageSummary.percentChangeTooltip'),
  precision = DEFAULT_USAGE_PRECISION,
}: UsageSummaryCardProps) {
  const {
    total: previousTotal,
    isLoading: isPreviousLoading,
    isError: isPreviousError,
  } = useUsageData({
    usageTab,
    aggregationType,
    from: previousFromTimestampUTC,
    to: previousToTimestampUTC,
    granularity: Granularity.DAILY,
  });

  return (
    <BaseSummaryCard
      title={title}
      titleTooltip={titleTooltip}
      total={total}
      percentChangeTooltip={percentChangeTooltip}
      isTotalLoading={isTotalLoading}
      isTotalError={isTotalError}
      previousTotal={previousTotal}
      isPreviousLoading={isPreviousLoading}
      isPreviousError={isPreviousError}
      precision={precision}
    />
  );
}

type DimensionSummaryCardProps = {
  title: string;
  total: number;
  isTotalLoading?: boolean;
  isTotalError?: boolean;
  isPreviousLoading?: boolean;
  isPreviousError?: boolean;
  metadata?: UsageSeriesList['metadata'][number];
  groupByMetadataKeys: string[];
  aggregationType: AggregationType;
  previousGroupedData?: UsageSeriesList;
  percentChangeTooltip?: string;
  precision?: number;
};

export function DimensionSummaryCard({
  title,
  total,
  isTotalLoading = false,
  isTotalError = false,
  isPreviousLoading = false,
  isPreviousError = false,
  metadata,
  groupByMetadataKeys,
  aggregationType,
  previousGroupedData,
  percentChangeTooltip = msg('billing.usageSummary.percentChangeTooltip'),
  precision,
}: DimensionSummaryCardProps) {
  const previousTotalIndex = metadata
    ? previousGroupedData?.metadata?.findIndex((previousMetadata) =>
        groupByMetadataKeys.every((key) => previousMetadata[key] === metadata[key]),
      )
    : undefined;

  const previousTotal =
    previousTotalIndex !== -1 && previousTotalIndex !== undefined
      ? getSeriesTotal({
          series: previousGroupedData?.series,
          aggregationType,
          metadataIdx: previousTotalIndex.toString(),
          granularity: Granularity.DAILY,
        })
      : 0;

  return (
    <BaseSummaryCard
      title={title}
      total={total}
      percentChangeTooltip={percentChangeTooltip}
      isTotalLoading={isTotalLoading}
      isTotalError={isTotalError}
      previousTotal={previousTotal}
      isPreviousLoading={isPreviousLoading}
      isPreviousError={isPreviousError}
      precision={precision}
    />
  );
}

type BaseSummaryCardProps = {
  title: string;
  titleTooltip?: string;
  formattedTotal?: string;
  percentChangeTooltip?: string;
  total: number;
  isTotalLoading?: boolean;
  isTotalError?: boolean;
  previousTotal?: number;
  isPreviousLoading?: boolean;
  isPreviousError?: boolean;
  precision?: number;
};

export function BaseSummaryCard({
  title,
  titleTooltip,
  formattedTotal,
  total,
  percentChangeTooltip,
  isTotalLoading = false,
  isTotalError = false,
  previousTotal = 0,
  isPreviousLoading = false,
  isPreviousError = false,
  precision = DEFAULT_USAGE_PRECISION,
}: BaseSummaryCardProps) {
  const precisionThreshold = 1 / Math.pow(10, precision);

  // This logic hides percent change when either total or previousTotal is below the precision threshold.
  // While this can hide large relative changes (.0001 -> .0003), it avoids confusing the user with
  // misleading percentages for values that visually round to zero.
  const percentChange =
    Math.abs(previousTotal) >= precisionThreshold && (Math.abs(total) >= precisionThreshold || total === 0)
      ? (total - previousTotal) / previousTotal
      : 0;
  const isPositive = percentChange >= 0;
  const showPercentChange =
    !isTotalLoading && !isTotalError && !isPreviousLoading && !isPreviousError && percentChange !== 0;

  const renderTotalValue = () => {
    if (isTotalLoading) {
      return <ProgressBar isIndeterminate size="small" aria-label={msg('billing.usageSummary.loadingLabel')} />;
    }

    if (isTotalError) {
      return (
        <Alert status="error" variant="inline">
          <Message id="billing.usageSummary.errorLoadingSummary" />
        </Alert>
      );
    }

    return formatNumber(total, { maximumFractionDigits: precision });
  };

  const tooltip = titleTooltip ?? title;
  const showFormattedTotal = formattedTotal !== undefined && !isTotalLoading && !isTotalError;

  return (
    <div className={clsx(styles.cardContainer, isTotalLoading && styles.loading)}>
      <TooltipTrigger>
        <Focusable>
          <div className={styles.title} role="button">
            {title}
          </div>
        </Focusable>
        <Tooltip>{tooltip}</Tooltip>
      </TooltipTrigger>
      <div className={styles.totalContainer}>
        {showFormattedTotal ? (
          <TooltipTrigger>
            <Focusable>
              <span className={styles.totalValue} role="button" aria-label={tooltip}>
                {formattedTotal}
              </span>
            </Focusable>
            <Tooltip>{tooltip}</Tooltip>
          </TooltipTrigger>
        ) : (
          <span className={styles.totalValue}>{renderTotalValue()}</span>
        )}

        {showPercentChange && (
          <span className={clsx(styles.percentChange, isPositive ? styles.positive : styles.negative)}>
            <TooltipTrigger>
              <Icon
                className={isPositive ? styles.positiveIcon : styles.negativeIcon}
                name={isPositive ? 'arrow-up-thin' : 'arrow-down-thin'}
                size="small"
              />
              <Focusable>
                <span role="button" className={styles.percentChangeValue}>
                  {formatFloatAsPercent(Math.abs(percentChange))}
                </span>
              </Focusable>
              <Tooltip>{percentChangeTooltip}</Tooltip>
            </TooltipTrigger>
          </span>
        )}
      </div>
    </div>
  );
}
